import { withActionDialogLock } from "@/action-dialog/actionDialogLock";
import {
  BulkDeleteCallable,
  BulkMoveCallable,
  CanvasFolderCallable,
  CreateWorkspaceCallable,
  DeleteFolderCallable,
  NewFolderCallable,
  ShareWorkspaceCallable,
} from "@/action-dialog/callables";
import type { UnifiedItem } from "@/pages/workspaces/types";
import type {
  Canvas,
  FileRecord,
  Folder,
  FolderResourceType,
  InboxItem,
  Workspace,
  WorkspaceMetadata,
} from "@cohesive/api-client";
import toast from "react-hot-toast";

export interface CreateWorkspaceParams {
  createWorkspace: {
    mutateAsync: (args: {
      name: string;
      description?: string;
      metadata?: WorkspaceMetadata;
    }) => Promise<Workspace>;
  };
  onSuccess?: (workspace: Workspace) => void | Promise<void>;
}

export function runCreateWorkspaceWorkflow(
  params: CreateWorkspaceParams,
): Promise<boolean> {
  const { createWorkspace, onSuccess } = params;

  return withActionDialogLock(() =>
    CreateWorkspaceCallable.call({
      mutationFn: async (call, values) => {
        try {
          const metadata: WorkspaceMetadata | undefined = values.icon
            ? { icon: values.icon }
            : undefined;
          const workspace = await createWorkspace.mutateAsync({
            name: values.name,
            description: values.description || undefined,
            metadata,
          });
          await onSuccess?.(workspace);
          call.end(true);
        } catch {
          toast.error("Failed to create workspace");
          throw new Error("Failed to create workspace");
        }
      },
    }),
  );
}

export interface ShareWorkspaceParams {
  workspace: Workspace;
}

export function runShareWorkspaceWorkflow(
  params: ShareWorkspaceParams,
): Promise<boolean> {
  return withActionDialogLock(() =>
    ShareWorkspaceCallable.call({ workspace: params.workspace }),
  );
}

export interface CreateFolderParams {
  workspaceId: string;
  currentPath: string;
}

export function runCreateFolderWorkflow(
  params: CreateFolderParams,
): Promise<boolean> {
  return withActionDialogLock(() =>
    NewFolderCallable.call({
      workspaceId: params.workspaceId,
      currentPath: params.currentPath,
    }),
  );
}

export interface CreateResourceFolderParams {
  workspaceId: string;
  resourceType: FolderResourceType;
  /** Parent for the new folder; null/absent = workspace root. */
  parentFolderId?: string | null;
}

/** Create a shared canvas/chat folder in a workspace. */
export function runCreateResourceFolderWorkflow(
  params: CreateResourceFolderParams,
): Promise<boolean> {
  return withActionDialogLock(() =>
    CanvasFolderCallable.call({
      workspaceId: params.workspaceId,
      resourceType: params.resourceType,
      parentFolderId: params.parentFolderId,
    }),
  );
}

export interface RenameResourceFolderParams {
  workspaceId: string;
  folder: Folder;
}

/** Rename a shared canvas/chat folder. */
export function runRenameResourceFolderWorkflow(
  params: RenameResourceFolderParams,
): Promise<boolean> {
  return withActionDialogLock(() =>
    CanvasFolderCallable.call({
      workspaceId: params.workspaceId,
      folder: params.folder,
      resourceType: params.folder.resource_type,
    }),
  );
}

export interface DeleteResourceFolderParams {
  workspaceId: string;
  folder: Folder;
}

/** Confirm + delete a shared canvas/chat folder (contents re-parent up). */
export function runDeleteResourceFolderWorkflow(
  params: DeleteResourceFolderParams,
): Promise<boolean> {
  return withActionDialogLock(() =>
    DeleteFolderCallable.call({
      workspaceId: params.workspaceId,
      folder: params.folder,
    }),
  );
}

export interface BulkMoveParams {
  workspaceId: string;
  allRecords: FileRecord[];
  items: UnifiedItem[];
  onMoved?: () => void;
}

export function runBulkMoveWorkflow(params: BulkMoveParams): Promise<boolean> {
  const { workspaceId, allRecords, items, onMoved } = params;

  return withActionDialogLock(async () => {
    const moved = await BulkMoveCallable.call({
      workspaceId,
      allRecords,
      items,
    });
    if (moved) {
      onMoved?.();
    }
    return moved;
  });
}

export interface BulkDeleteParams {
  items: UnifiedItem[];
  onDeleteCanvas: (canvas: Canvas) => Promise<void>;
  onDeleteFileRecord: (record: FileRecord) => Promise<void>;
  onDeleteChat?: (chat: InboxItem) => Promise<void>;
  onDeleted?: () => void;
}

export function runBulkDeleteWorkflow(
  params: BulkDeleteParams,
): Promise<boolean> {
  const { items, onDeleteCanvas, onDeleteFileRecord, onDeleteChat, onDeleted } =
    params;

  return withActionDialogLock(async () => {
    const deleted = await BulkDeleteCallable.call({
      items,
      onDeleteCanvas,
      onDeleteFileRecord,
      onDeleteChat,
    });
    if (deleted) {
      onDeleted?.();
    }
    return deleted;
  });
}
